import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SaveOutlined } from '@ant-design/icons';
import DatepickerMultipleField from '@components/common/form/DatepickerMultipleField';
import DropdownField from '@components/common/form/DropdownField';
import FileUploadField from '@components/common/form/FileUploadField';
import TimePickerField from '@components/common/form/TimePickerField';
import useTranslate from '@hooks/useTranslate';
import { CheckboxField } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { hideAppLoading, showAppLoading } from '@store/actions/app';
import { getDefaultTimeWork } from '@utils';
import { getDisabledHours, getDisabledMinutes } from '@utils/dayHelper';
import { Button, Col, Flex, Form, Row, Space, Switch } from 'antd';
import moment from 'moment';

import {
    AppConstants,
    DATE_FORMAT_DISPLAY,
    MediaKinds,
    TIME_FORMAT_DISPLAY,
    UploadFileTypes,
} from '../../../constants';
import {
    daysOfWeekTimeWork as daysOfWeekTimeWorks,
    mediaCodes as mediaCode,
    mediaKinds,
    orderMethods,
} from '../../../constants/masterData';
import { showErrorMessage } from '../../../services/notifyService';

import './index.scss';

const isMidnight = (value) => !!value && value.hour() === 0 && value.minute() === 0;

const getDisabledMinutesForTo = (hour, fromValue) => {
    const baseDisabled = getDisabledMinutes(hour, fromValue) || [];

    if (
        fromValue &&
        fromValue.hour() === hour &&
        !isMidnight(fromValue)
    ) {
        return [ ...new Set([ ...baseDisabled, fromValue.minute() ]) ];
    }

    return baseDisabled;
};

const QrCodeLiveSettingForm = ({
    formId,
    settingObj,
    onSubmit,
    uploadFile,
    setIsChangedFormValues,
    deviceList,
    updateDeviceMedia,
    isChangedFormValues,
    loadingUpdate,
}) => {
    const [ form ] = Form.useForm();
    const [ medias, setMedias ] = useState([]);
    const [ otherData, setOtherData ] = useState({ tablet_random: {} });
    const translate = useTranslate();
    const dispatch = useDispatch();
    const enableTimeWork = Form.useWatch(['tablet_random', 'enable_time_work'], form);

    const mediaKindValues = translate.formatKeys(mediaKinds, [ 'label' ]);
    const mediaCodes = translate.formatKeys(mediaCode, [ 'label' ]);
    const daysOfWeekTimeWork = translate.formatKeys(daysOfWeekTimeWorks, [ 'label' ]);


    useEffect(() => {
        const { tablet_random } = settingObj || {};
        const mediaLength = 5;
        const mediaListSaved = tablet_random?.medias || [];
        const filledMediaList = Array.from(
            { length: mediaLength },
            (_, i) => mediaListSaved[i] || { media_url: '', media_out_url: '', media_kind: MediaKinds.IMAGE },
        );

        const initialMedias = filledMediaList.map((media, index) => convertMediaToMediaForm(media, index));

        setMedias(initialMedias);
        setOtherData({
            tablet_random: {
                ...tablet_random,
                medias: filledMediaList.map((data, index) => ({
                    media_url: data?.media_url || '',
                    media_out_url: data?.media_out_url || '',
                    media_kind: data?.media_kind || MediaKinds.IMAGE,
                    media_code: data?.media_code || mediaCodes[index]?.value,
                })),
            },
        });
        form.setFieldsValue({
            ...settingObj,
            tablet_random: {
                ...tablet_random,
            },
        });
    }, [ settingObj, form ]);

    const convertMediaToMediaForm = (media, index) => {
        return {
            mediaFileList: media.media_url
                ? [ { uid: 1, name: media.media_url, url: `${AppConstants.contentRootUrl}${media.media_url}` } ]
                : [],
            mediaOutFileList: media.media_out_url
                ? [ { uid: 2, name: media.media_out_url, url: `${AppConstants.contentRootUrl}${media.media_out_url}` } ]
                : [],
            mediaKind: media.media_kind || MediaKinds.IMAGE,
            mediaCode: mediaCodes[index]?.value,
        };
    };

    const handleSubmit = (formValues) => {
        const { enable_time_work, ...restTabletRandom } = formValues.tablet_random || {};

        const finalSubmitData = {
            ...settingObj,
            ...formValues,
            ...otherData,
            tablet_random: {
                ...otherData.tablet_random,
                ...restTabletRandom,
                time_work: enable_time_work ? restTabletRandom.time_work : null,
                medias: otherData.tablet_random.medias,
            },
        };
        delete finalSubmitData.tablet_random.enable_time_work;
        onSubmit(finalSubmitData);
    };

    const onValuesChange = () => {
        setIsChangedFormValues(true);
    };

    const setMediaData = (name, value, index) => {
        setOtherData((prev) => ({
            ...prev,
            tablet_random: {
                ...prev.tablet_random,
                medias: prev.tablet_random.medias.map((item, i) => (i === index ? { ...item, [name]: value } : item)),
            },
        }));
        onValuesChange();
    };

    const setMediaState = (media, index) => {
        setMedias((prev) => {
            const newMedias = [ ...prev ];
            newMedias[index] = { ...newMedias[index], ...media };
            return newMedias;
        });
    };

    const handleChangeMediaFile = (info, index) => {
        if (info.file.status === 'removed') {
            setMediaData('media_url', '', index);
            setMediaState({ mediaFileList: [] }, index);
        } else {
            setMediaState({ mediaFileList: [ info.file ] }, index);
        }
    };

    const handleChangeMediaOutFile = (info, index) => {
        if (info.file.status === 'removed') {
            setMediaData('media_out_url', '', index);
            setMediaState({ mediaOutFileList: [] }, index);
        } else {
            setMediaState({ mediaOutFileList: [ info.file ] }, index);
        }
    };

    const handleChangeMediaKind = (kind, index) => {
        setMediaData('media_url', '', index);
        setMediaData('media_out_url', '', index);
        setMediaData('media_kind', kind, index);
        setMediaState({ mediaFileList: [], mediaOutFileList: [], mediaKind: kind }, index);
    };

    const uploadFileMedia = (fileObj, onSuccess, index) => {
        const file = fileObj.file || fileObj;
        const { mediaKind, mediaFileList } = medias[index];
        dispatch(showAppLoading(true));
        uploadFile({
            data: {
                file: file,
                type: mediaKind === MediaKinds.IMAGE ? UploadFileTypes.LOGO : UploadFileTypes.VIDEO,
            },
            onCompleted: (result) => {
                setMediaData('media_url', result.data.filePath, index);
                setMediaState(
                    {
                        mediaFileList: [
                            {
                                uid: 1,
                                name: result.data.filePath,
                                url: `${AppConstants.contentRootUrl}${result.data.filePath}`,
                            },
                        ],
                    },
                    index,
                );
                onSuccess();
                dispatch(hideAppLoading(true));
            },
            onError: (err) => {
                if (err && err.message) {
                    showErrorMessage(translate.formatMessage(commonMessage.uploadFileFail), translate);
                    if (mediaFileList && mediaFileList.length) {
                        setMediaState(
                            {
                                mediaFileList: [ { ...mediaFileList[0], status: 'error' } ],
                            },
                            index,
                        );
                    } else {
                        setMediaState({ mediaFileList: [] }, index);
                    }
                }
                dispatch(hideAppLoading(true));
            },
        });
    };

    const uploadFileMediaOut = (file, onSuccess, index) => {
        const { mediaKind, mediaOutFileList } = medias[index];
        dispatch(showAppLoading(true));
        uploadFile({
            data: {
                file: file,
                type: mediaKind === MediaKinds.IMAGE ? UploadFileTypes.LOGO : UploadFileTypes.VIDEO,
            },
            onCompleted: (result) => {
                setMediaData('media_out_url', result.data.filePath, index);
                setMediaState(
                    {
                        mediaOutFileList: [
                            {
                                uid: 2,
                                name: result.data.filePath,
                                url: `${AppConstants.contentRootUrl}${result.data.filePath}`,
                            },
                        ],
                    },
                    index,
                );
                onSuccess();
                dispatch(hideAppLoading(true));
            },
            onError: (err) => {
                if (err && err.message) {
                    showErrorMessage(translate.formatMessage(commonMessage.uploadFileFail), translate);
                    if (mediaOutFileList && mediaOutFileList.length) {
                        setMediaState(
                            {
                                mediaOutFileList: [ { ...mediaOutFileList[0], status: 'error' } ],
                            },
                            index,
                        );
                    } else {
                        setMediaState({ mediaOutFileList: [] }, index);
                    }
                }
                dispatch(hideAppLoading(true));
            },
        });
    };

    const onSelectTimeWorkTabletRandom = (fieldName, value) => {
        const tabletRandom = form.getFieldValue('tablet_random');
        const [ dayKey, dayIndexKey, frameKey ] = fieldName;
        if (frameKey === 'from') {
            const to = tabletRandom.time_work[dayKey][dayIndexKey].to;
            // if (to && to.format(TIME_FORMAT_DISPLAY) < value.format(TIME_FORMAT_DISPLAY)) {
            //     tabletRandom.time_work[dayKey][dayIndexKey].to = null;
            // }
            const toIsInvalid =
                to &&
                value &&
                (
                    to.isBefore(value) ||
                    (to.isSame(value) && !isMidnight(value))
                );

            if (toIsInvalid) {
                tabletRandom.time_work[dayKey][dayIndexKey].to = null;
            }
        } else if (frameKey === 'to') {
            const from = tabletRandom.time_work[dayKey][dayIndexKey].from;
            // if (from && value.format(TIME_FORMAT_DISPLAY) < from.format(TIME_FORMAT_DISPLAY)) {
            //     value = from;
            // }
            if (from && value) {
                if (value.isBefore(from)) {
                    value = from;
                } else if (value.isSame(from) && !isMidnight(from)) {
                    return;
                }
            }
        }
        tabletRandom.time_work[dayKey][dayIndexKey][frameKey] = value;
        form.setFieldsValue({ tablet_random: tabletRandom });
        onValuesChange();
    };

    // Áp dụng tất cả thời gian từ thứ Hai
    const handleApplyAll = (e) => {
        e.preventDefault();
        const tabletRandom = form.getFieldValue('tablet_random');
        const { monday = [] } = tabletRandom.time_work;

        for (let { value } of daysOfWeekTimeWork) {
            tabletRandom.time_work[value] = monday.map((frame) => ({
                from: moment(frame.from),
                to: moment(frame.to),
            }));
        }

        form.setFieldsValue({ tablet_random: tabletRandom });
        onValuesChange();
    };

    const checkCanApplyAll = () => {
        const tablet_random = form.getFieldValue('tablet_random');
        const { monday } = tablet_random?.time_work || {};
        if (!monday) return false;
        return monday.every((frame) => !!frame.from && !!frame.to);
    };

    const renderTimeWork = () => {
        const isEnabled = enableTimeWork;
        return (
            <table className="happy-hours-table">
                <thead>
                    <tr>
                        <th width="14%">{translate.formatMessage(commonMessage.dayOfWeek)}</th>
                        <th>{translate.formatMessage(commonMessage.timeFrame)}</th>
                    </tr>
                </thead>
                <tbody>
                    {daysOfWeekTimeWork.map((day, dayIndex) => (
                        <tr key={day.value}>
                            <td>{day.label}</td>
                            <td style={{ padding: '10px' }}>
                                <Form.List name={[ 'tablet_random', 'time_work', day.value ]}>
                                    {(fields) => (
                                        <div className="no-margin-form-item">
                                            <Space className="box-flex" size={24}>
                                                {fields.map((field, index) => (
                                                    <div key={field.key}>
                                                        <div className="frame-label">
                                                            {translate.formatMessage(commonMessage.timeFrame, {
                                                                no: index + 1,
                                                            })}
                                                        </div>
                                                        <Space className="box-flex">
                                                            <TimePickerField
                                                                disabled={!isEnabled}
                                                                size="small"
                                                                fieldName={[ field.name, 'from' ]}
                                                                onSelect={(value) =>
                                                                    onSelectTimeWorkTabletRandom(
                                                                        [ day.value, field.name, 'from' ],
                                                                        value,
                                                                    )
                                                                }
                                                                width="100%"
                                                                required
                                                                placeholder={translate.formatMessage(
                                                                    commonMessage.from,
                                                                )}
                                                                requiredMsg={translate.formatMessage(
                                                                    commonMessage.requireFrom,
                                                                )}
                                                                validateTrigger={[ 'onChange', 'onBlur' ]}
                                                                onClick={onValuesChange}
                                                            />
                                                            <TimePickerField
                                                                disabled={!isEnabled}
                                                                size="small"
                                                                fieldName={[ field.name, 'to' ]}
                                                                onSelect={(value) =>
                                                                    onSelectTimeWorkTabletRandom(
                                                                        [ day.value, field.name, 'to' ],
                                                                        value,
                                                                    )
                                                                }
                                                                width="100%"
                                                                required
                                                                placeholder={translate.formatMessage(commonMessage.to)}
                                                                requiredMsg={translate.formatMessage(
                                                                    commonMessage.requireTo,
                                                                )}
                                                                validateTrigger={[ 'onChange', 'onBlur' ]}
                                                                disabledHours={() => {
                                                                    const tabletRandom =
                                                                        form.getFieldValue('tablet_random');
                                                                    const from =
                                                                        tabletRandom.time_work[day.value][field.name]
                                                                            .from;
                                                                    return getDisabledHours(from);
                                                                }}
                                                                disabledMinutes={(hour) => {
                                                                    const tabletRandom =
                                                                        form.getFieldValue('tablet_random');
                                                                    const from =
                                                                        tabletRandom.time_work[day.value][field.name]
                                                                            .from;
                                                                    return getDisabledMinutesForTo(hour, from);
                                                                }}
                                                                onClick={onValuesChange}
                                                            />
                                                        </Space>
                                                    </div>
                                                ))}
                                                {!dayIndex && (
                                                    <div className="wrap-btn-apply-all">
                                                        <Button
                                                            disabled={!isEnabled || !checkCanApplyAll()}
                                                            type="primary"
                                                            size="small"
                                                            onClick={handleApplyAll}
                                                        >
                                                            {translate.formatMessage(commonMessage.applyAll)}
                                                        </Button>
                                                    </div>
                                                )}
                                            </Space>
                                        </div>
                                    )}
                                </Form.List>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    const renderMediaItem = ({ index }) => {
        const media = medias[index] || {
            mediaFileList: [],
            mediaOutFileList: [],
            mediaKind: MediaKinds.IMAGE,
        };
        const { mediaFileList, mediaOutFileList, mediaKind } = media;

        return (
            <tr>
                <td>{translate.formatMessage(commonMessage.mediaCount, { count: index + 1 })}</td>
                <td>
                    <DropdownField
                        fieldName={[ 'tablet_random', 'medias', index, 'media_kind' ]}
                        options={mediaKindValues}
                        onChange={(kind) => handleChangeMediaKind(kind, index)}
                        style={{ marginBottom: '0px' }}
                    />
                </td>
                <td>
                    <FileUploadField
                        accept={mediaKind === MediaKinds.IMAGE ? 'image/*' : 'video/*'}
                        fileList={mediaFileList}
                        onChange={(info) => handleChangeMediaFile(info, index)}
                        uploadFile={(fileObj, onSuccess) => uploadFileMedia(fileObj, onSuccess, index)}
                        style={{ marginBottom: '0px' }}
                    />
                </td>
                <td>
                    <FileUploadField
                        accept={mediaKind === MediaKinds.IMAGE ? 'image/*' : 'video/*'}
                        fileList={mediaOutFileList}
                        onChange={(info) => handleChangeMediaOutFile(info, index)}
                        uploadFile={(fileObj, onSuccess) => uploadFileMediaOut(fileObj, onSuccess, index)}
                        style={{ marginBottom: '0px' }}
                    />
                </td>
            </tr>
        );
    };

    const renderDeviceList = () => {
        return (
            <table className="device-media-table">
                <thead>
                    <tr>
                        <th>{translate.formatMessage(commonMessage.deviceName)}</th>
                        <th>{translate.formatMessage(commonMessage.mediaKind)}</th>
                    </tr>
                </thead>
                <tbody>
                    {deviceList.map((device) => (
                        <tr key={device.id}>
                            <td>{device.deviceName}</td>
                            <td>
                                <DropdownField
                                    placeholder={translate.formatMessage(commonMessage.mediaCode)}
                                    fieldName={[ 'tablet_random', 'device_medias', device.id + '', 'media_code' ]}
                                    options={mediaCodes}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    return (
        <Form
            id={formId}
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={settingObj}
            onValuesChange={onValuesChange}
        >
            <Row gutter={16}>
                <Col span={24}>
                    <Form.Item label={translate.formatMessage(commonMessage.media)}>
                        <div className="wrap-media-table">
                            <table className="media-table">
                                <thead>
                                    <tr>
                                        <th>{translate.formatMessage(commonMessage.mediaCode)}</th>
                                        <th>{translate.formatMessage(commonMessage.mediaKind)}</th>
                                        <th>{translate.formatMessage(commonMessage.media)}</th>
                                        <th>{translate.formatMessage(commonMessage.mediaOut)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {renderMediaItem({ index: 0 })}
                                    {renderMediaItem({ index: 1 })}
                                    {renderMediaItem({ index: 2 })}
                                    {renderMediaItem({ index: 3 })}
                                    {renderMediaItem({ index: 4 })}
                                </tbody>
                            </table>
                        </div>
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={24}>
                    <Form.Item label={translate.formatMessage(commonMessage.mediaDevice)}>
                        {renderDeviceList()}
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={24}>
                <Col span={8}>
                    <CheckboxField
                        optionLabel={translate.formatMessage(commonMessage.skipMethod)}
                        name={[ 'tablet_random', 'skip_method' ]}
                    />
                </Col>
            </Row>
            <Row gutter={24}>
                <Col span={8}>
                    <DropdownField
                        mode="multiple"
                        fieldName="paymentQrlive"
                        label={translate.formatMessage(commonMessage.paymentMethod)}
                        // options={Utils.i18List(orderMethods)}
                    />
                </Col>
            </Row>
            <Row gutter={16}>
                {/* <Col span={24}>
                    <Form.Item 
                        valuePropName="checked"
                        name={['tablet_random', 'enable_time_work']}
                    >
                        <Switch
                            onChange={(checked) => {
                                if (checked) {
                                    const currentTablet = form.getFieldValue('tablet_random');
                                    form.setFieldsValue({
                                        tablet_random: {
                                            ...currentTablet,
                                            time_work: getDefaultTimeWork(),
                                        },
                                    });
                                } else {
                                    const currentTablet = form.getFieldValue('tablet_random');
                                    form.setFieldsValue({
                                        tablet_random: {
                                            ...currentTablet,
                                            time_work: null,
                                        },
                                    });
                                }
                                onValuesChange();
                            }}
                        />
                    </Form.Item>
                </Col> */}
                <Col span={24}>
                    <Form.Item 
                        label={
                            <Flex align="center" gap={12}>
                                <span>{translate.formatMessage(commonMessage.timeWork)}</span>
                                <Form.Item 
                                    name={['tablet_random', 'enable_time_work']} 
                                    valuePropName="checked" 
                                    noStyle
                                >
                                    <Switch
                                        onChange={(checked) => {
                                            const currentTablet = form.getFieldValue('tablet_random');
                                            form.setFieldsValue({
                                                tablet_random: {
                                                    ...currentTablet,
                                                    time_work: checked ? getDefaultTimeWork() : settingObj.general_time_work,
                                                    enable_time_work: checked,
                                                },
                                            });
                                            onValuesChange();
                                        }}
                                    />
                                </Form.Item>
                            </Flex>
                        }
                        style={{ display: 'block' }}>
                        {renderTimeWork()}
                    </Form.Item>
                </Col>
                <Col span={14}>
                    <DatepickerMultipleField
                        disabledSelect={!enableTimeWork}
                        disabled={!enableTimeWork}
                        label={translate.formatMessage(commonMessage.dayOffs)}
                        fieldName={[ 'tablet_random', 'time_work', 'dayOffs' ]}
                        format={DATE_FORMAT_DISPLAY}
                    />
                </Col>
            </Row>

            <Flex justify="end">
                <Button
                    style={{ marginLeft: '10rem', marginTop: 12 }}
                    key="submit"
                    htmlType="submit"
                    type="primary"
                    disabled={!isChangedFormValues}
                    loading={loadingUpdate}
                >
                    <div>
                        <SaveOutlined style={{ marginRight: '6px' }} />
                        {translate.formatMessage(commonMessage.update)}
                    </div>
                </Button>
            </Flex>
        </Form>
    );
};

export default QrCodeLiveSettingForm;
